import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PencilIcon } from '@heroicons/react/24/outline'
import { Helmet } from 'react-helmet'
import { Link as RouterLink, Prompt, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { ColDef, GridApi, GridReadyEvent, GridOptions } from 'ag-grid-community'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  IconButton,
  Link,
  Select,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import { DynamicWidthPageContainer } from 'components/TailwindUIToolkit/Layouts/DynamicWidthPageContainer'
import Grid from 'components/TailwindUIToolkit/Grid/BaseGrid'
import SetFilter from 'components/AgGrid/filters/SetFilter'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { useAuthContext } from 'context/auth'
import { canAccessVacancyReadiness } from 'lib/admin-perms'
import {
  readinessDate,
  listedRentLabel,
  readinessErrorMessage,
  vacancyReadinessApi,
  ReadinessSelection,
  ReadinessSort,
  ReadinessStatusFilter,
  ReadinessFilterOptions
} from 'lib/vacancy-readiness'
import { StoreState } from 'store'
import ReadinessPanel from './ReadinessPanel'

const PAGE_SIZE = 50
const statusLabels: Record<NonNullable<ReadinessSelection['state']>, string> = {
  current: 'Current',
  upcoming: 'Upcoming',
  historical: 'Past',
  canceled: 'Canceled',
  date_conflict: '—',
  unresolved: '—'
}
const sortColumns: Record<string, ReadinessSort> = {
  state: 'status',
  address_line_1: 'address',
  start_date: 'start_date',
  end_date: 'end_date',
  estimated_tour_ready_date: 'estimated_tour_ready_date',
  expected_move_in_date: 'expected_move_in_date',
  current_confirmed_tour_ready_date: 'current_confirmed_tour_ready_date',
  current_confirmed_rent_ready_date: 'current_confirmed_rent_ready_date'
}

type SetModels = Record<string, { filter?: string[] }>
const selectedFilters = (model: SetModels) => ({
  fund_ids: model.fund_id?.filter?.length ? [...model.fund_id.filter].sort() : undefined,
  market_ids: model.market_id?.filter?.length ? [...model.market_id.filter].sort() : undefined,
  episode_kinds: model.episode_kind?.filter?.length
    ? [...model.episode_kind.filter].sort()
    : undefined
})

// Render one server-paged result set locally so AG Grid can measure wrapped cells.
// The API still owns ordering/filtering; never sort or filter just the loaded page.
const gridOptions: GridOptions<ReadinessSelection> = {
  rowModelType: 'clientSide',
  rowHeight: 64,
  suppressMultiSort: true,
  suppressScrollOnNewData: true,
  sortingOrder: ['asc', 'desc'],
  sideBar: false,
  rowSelection: 'single'
}
const defaultColumn: ColDef<ReadinessSelection> = {
  sortable: true,
  comparator: () => 0,
  filter: false,
  resizable: true,
  autoHeight: true,
  wrapText: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellStyle: { display: 'flex', alignItems: 'center', lineHeight: '20px' }
}

const VacancyReadiness = () => {
  const { search: urlSearch } = useLocation()
  const homeId = new URLSearchParams(urlSearch).get('portfolio_home_id') || undefined
  const [searchText, setSearchText] = useState('')
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<ReadinessStatusFilter>('current_and_upcoming')
  const [selected, setSelected] = useState<ReadinessSelection | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [gridApi, setGridApi] = useState<GridApi<ReadinessSelection> | null>(null)
  const [loading, setLoading] = useState(false)
  const gridContainer = useRef<HTMLDivElement>(null)
  const [gridHeight, setGridHeight] = useState(720)
  const filterOptions = useRef<ReadinessFilterOptions>({ funds: [], markets: [] })
  const filterNames = useRef<Record<'funds' | 'markets', Map<string, string>>>({
    funds: new Map(),
    markets: new Map()
  })
  const [canViewRentPrices, setCanViewRentPrices] = useState(false)
  const [rows, setRows] = useState<ReadinessSelection[]>([])
  const [filters, setFilters] = useState<ReturnType<typeof selectedFilters>>(() =>
    selectedFilters({})
  )
  const [sort, setSort] = useState<{ colId: string; direction: 'asc' | 'desc' }>({
    colId: 'state',
    direction: 'asc'
  })
  const [refresh, setRefresh] = useState(0)
  const browseKey = JSON.stringify({ homeId, query, status, filters, sort })
  const [cursor, setCursor] = useState({ key: '', page: 1 })
  const page = cursor.key === browseKey ? cursor.page : 1
  useEffect(() => {
    setCursor({ key: browseKey, page: 1 })
  }, [browseKey])
  const scrollKey = useRef('')
  const [hasColumnFilters, setHasColumnFilters] = useState(false)
  const [total, setTotal] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { firebaseUser } = useAuthContext()
  const userId = firebaseUser?.uid
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const canRead = canAccessVacancyReadiness(admin)
  useLayoutEffect(() => {
    const container = gridContainer.current
    if (!canRead || !container) return
    // Keep enough rows visible even on shorter screens; the page can scroll to
    // the paging bar instead of compressing the grid to just a few rows.
    const measure = () => {
      setGridHeight(
        Math.max(
          640,
          Math.min(960, window.innerHeight - container.getBoundingClientRect().top - 24)
        )
      )
    }
    measure()
    let frame = 0
    const scheduleMeasure = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(measure)
    }
    const observer = new ResizeObserver(scheduleMeasure)
    observer.observe(container.parentElement || container)
    window.addEventListener('resize', scheduleMeasure)
    return () => {
      observer.disconnect()
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [canRead])
  useEffect(() => {
    if (!canRead) {
      setDetailsOpen(false)
      setSelected(null)
    }
  }, [canRead])

  useEffect(() => {
    if (editing || detailsOpen) return
    const timer = window.setTimeout(() => setQuery(searchText.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchText, editing, detailsOpen])
  useEffect(() => {
    setSelected(null)
    setDetailsOpen(false)
  }, [homeId, query, status])

  const openDetails = useCallback((vacancy: ReadinessSelection) => {
    setSelected(vacancy)
    setDetailsOpen(true)
  }, [])
  const closeDetails = useCallback(() => {
    if (!editing) setDetailsOpen(false)
  }, [editing])
  const columns = useMemo<ColDef<ReadinessSelection>[]>(
    () => [
      {
        field: 'address_line_1',
        headerName: 'Address',
        pinned: 'left',
        lockPinned: true,
        width: 310,
        minWidth: 260,
        cellRenderer: ({ data }: { data?: ReadinessSelection }) =>
          data ? (
            <HStack py={2} spacing={2} width='full' align='start'>
              <IconButton
                size='xs'
                mt={0.5}
                variant='outline'
                colorScheme='teal'
                flexShrink={0}
                aria-label={`Edit listing for ${data.address_line_1}`}
                icon={<PencilIcon width={16} />}
                onClick={(event) => {
                  event.stopPropagation()
                  openDetails(data)
                }}
                onKeyDown={(event) => event.stopPropagation()}
              />
              <Stack spacing={0} minW={0} lineHeight='20px'>
                {data.home_id ? (
                  <Link
                    as={RouterLink}
                    to={`/home/${data.home_id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    color='teal.700'
                    fontSize='14px'
                    fontWeight='semibold'
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {data.address_line_1}
                  </Link>
                ) : (
                  <Text fontSize='14px' fontWeight='semibold'>
                    {data.address_line_1}
                  </Text>
                )}
                <Text fontSize='12px' color='gray.600'>
                  {data.address_line_2}
                </Text>
              </Stack>
            </HStack>
          ) : null
      },
      {
        field: 'state',
        headerName: 'Status',
        initialSort: 'asc',
        sortingOrder: ['asc', 'desc'],
        width: 145,
        minWidth: 140,
        cellRenderer: ({ data }: { data?: ReadinessSelection }) =>
          data && (data.state === 'date_conflict' || !data.state || data.state === 'unresolved') ? (
            <Text color='gray.500'>—</Text>
          ) : data ? (
            <Badge
              colorScheme={
                data.state === 'current' ? 'green' : data.state === 'upcoming' ? 'blue' : 'gray'
              }
            >
              {statusLabels[data.state || 'unresolved']}
            </Badge>
          ) : null
      },
      {
        field: 'listed_rent',
        headerName: 'Listed rent',
        headerTooltip: 'Current asking rent for this home, per month',
        width: 150,
        minWidth: 140,
        sortable: false,
        hide: !canViewRentPrices,
        cellRenderer: ({ value }: { value?: string | null }) => (
          <Text fontSize='sm' fontWeight='semibold'>
            {listedRentLabel(value)}
          </Text>
        )
      },
      ...(['fund', 'market'] as const).map(
        (kind): ColDef<ReadinessSelection> => ({
          colId: `${kind}_id`,
          headerName: kind === 'fund' ? 'Fund' : 'Market',
          width: kind === 'fund' ? 180 : 155,
          sortable: false,
          filter: SetFilter,
          menuTabs: ['filterMenuTab'],
          valueGetter: ({ data }) => data?.[`${kind}_id`] || 'unassigned',
          filterParams: {
            serverSide: true,
            values: () =>
              filterOptions.current[kind === 'fund' ? 'funds' : 'markets'].map((item) => item.id),
            formatValue: (value: string) =>
              filterNames.current[kind === 'fund' ? 'funds' : 'markets'].get(value) ||
              (value === 'unassigned' ? 'Unassigned' : value),
            badgeOptions: { size: 'xs', formatFn: (value: string) => value }
          },
          cellRenderer: ({ data }: { data?: ReadinessSelection }) =>
            data ? (
              <Box
                py={2}
                width='full'
                sx={{
                  '& > div': {
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    fontSize: '11px',
                    lineHeight: '16px',
                    padding: '2px 8px'
                  },
                  '& > div > div': { fontSize: '11px', lineHeight: '16px' },
                  '&, & *': { wordBreak: 'normal', overflowWrap: 'anywhere' }
                }}
              >
                {mapContentToBadge(data[`${kind}_name`] || 'Unassigned', {
                  size: 'xs',
                  formatFn: (value: string) => value
                })}
              </Box>
            ) : null
        })
      ),
      ...(
        [
          ['estimated_tour_ready_date', 'current_confirmed_tour_ready_date', 'Tour Ready Date'],
          ['expected_move_in_date', 'current_confirmed_rent_ready_date', 'Rent Ready Date']
        ] as const
      ).map(
        ([estimate, actual, headerName]): ColDef<ReadinessSelection> => ({
          field: estimate,
          headerName,
          headerTooltip: 'Sort by estimated date',
          width: 250,
          minWidth: 230,
          cellRenderer: ({ data }: { data?: ReadinessSelection }) =>
            data ? (
              <Stack spacing={0.5} py={2} fontSize='12px' lineHeight='18px'>
                <Text>
                  <Text as='span' color='gray.600'>
                    Estimated:{' '}
                  </Text>
                  {data[estimate] ? readinessDate(data[estimate]) : 'Not entered'}
                </Text>
                <Text>
                  <Text as='span' color='gray.600'>
                    Actual:{' '}
                  </Text>
                  {data[actual] ? readinessDate(data[actual]) : 'Not confirmed'}
                </Text>
              </Stack>
            ) : null
        })
      ),
      {
        field: 'episode_kind',
        headerName: 'Vacancy type',
        width: 140,
        sortable: false,
        filter: SetFilter,
        menuTabs: ['filterMenuTab'],
        filterParams: {
          serverSide: true,
          values: ['initial', 'rental'],
          formatValue: (value: string) => (value === 'initial' ? 'Initial' : 'Rental')
        },
        cellRenderer: ({ value }: { value?: string }) => (
          <Badge colorScheme={value === 'initial' ? 'purple' : 'cyan'}>
            {value === 'initial' ? 'Initial' : 'Rental'}
          </Badge>
        )
      },
      {
        colId: 'start_date',
        headerName: 'Vacancy Period',
        headerTooltip: 'Sort by vacancy start date',
        width: 245,
        minWidth: 225,
        cellRenderer: ({ data }: { data?: ReadinessSelection }) =>
          data ? (
            <Stack spacing={0.5} py={2} fontSize='12px' lineHeight='18px'>
              <Text>
                <Text as='span' color='gray.600'>
                  From:{' '}
                </Text>
                {readinessDate(data.move_out_date || data.ownership_start || null)}
              </Text>
              <Text>
                <Text as='span' color='gray.600'>
                  To:{' '}
                </Text>
                {data.next_move_in_date
                  ? readinessDate(data.next_move_in_date)
                  : data.state === 'current'
                  ? 'Present'
                  : 'Not scheduled'}
              </Text>
            </Stack>
          ) : null
      }
    ],
    [openDetails, canViewRentPrices]
  )

  useEffect(() => {
    if (!gridApi || !userId || !canRead) return
    let active = true
    const controller = new AbortController()
    setRows([])
    setTotal(null)
    setError(null)
    setLoading(true)
    gridApi.showLoadingOverlay()
    const load = async () => {
      try {
        const result = await vacancyReadinessApi.list(
          {
            q: query || undefined,
            portfolio_home_id: homeId,
            status,
            ...filters,
            sort_by: sortColumns[sort.colId] || 'status',
            sort_direction: sort.direction,
            page,
            per_page: PAGE_SIZE
          },
          controller.signal
        )
        if (!active) return
        const lastPage = Math.max(1, Math.ceil(result.total_count / PAGE_SIZE))
        if (page > lastPage) {
          setCursor({ key: browseKey, page: lastPage })
          return
        }
        filterOptions.current = result.filters || filterOptions.current
        for (const kind of ['funds', 'markets'] as const) {
          for (const option of filterOptions.current[kind]) {
            filterNames.current[kind].set(option.id, option.name)
          }
        }
        setCanViewRentPrices(!!result.can_view_rent_prices)
        setTotal(result.total_count)
        setRows(result.items)
        if (result.total_count === 0) gridApi.showNoRowsOverlay()
        else gridApi.hideOverlay()
      } catch (requestError) {
        if (!active) return
        setError(readinessErrorMessage(requestError))
        gridApi.hideOverlay()
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => {
      active = false
      controller.abort()
    }
  }, [gridApi, userId, canRead, homeId, query, status, filters, sort, page, browseKey, refresh])

  const onGridReady = useCallback(
    ({ api }: GridReadyEvent<ReadinessSelection>) => setGridApi(api),
    []
  )

  return (
    <DynamicWidthPageContainer
      pathToPage={[
        { name: 'Homes', href: '/homes' },
        { name: 'Vacancy Manager', href: '/vacancy-readiness' }
      ]}
    >
      <Prompt
        when={editing}
        message='Leave this page and discard your unsaved readiness changes?'
      />
      <Helmet>
        <title>Vacancy Manager | Hudson</title>
      </Helmet>
      <Stack spacing={4}>
        <Box>
          <Heading size='md'>Vacancy Manager</Heading>
          <Text mt={1} fontSize='sm' color='gray.600'>
            Find a vacancy to manage rent prices, update readiness dates or review change history.
          </Text>
        </Box>
        {!canRead ? (
          <Alert status='warning'>
            <AlertIcon />
            You do not have permission to access Vacancy Manager.
          </Alert>
        ) : (
          <>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align='end'>
              <FormControl maxW={{ md: '28rem' }}>
                <FormLabel htmlFor='readiness-home-search' fontSize='sm'>
                  Find a home
                </FormLabel>
                <Input
                  size='sm'
                  id='readiness-home-search'
                  value={searchText}
                  maxLength={200}
                  placeholder='Search by address'
                  isDisabled={editing}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </FormControl>
              <FormControl maxW={{ md: '13rem' }}>
                <FormLabel htmlFor='readiness-status-filter' fontSize='sm'>
                  Vacancy status
                </FormLabel>
                <Select
                  size='sm'
                  id='readiness-status-filter'
                  value={status}
                  isDisabled={editing}
                  onChange={(event) => setStatus(event.target.value as ReadinessStatusFilter)}
                >
                  <option value='current_and_upcoming'>Current + upcoming</option>
                  <option value='all'>All vacancies</option>
                  <option value='canceled'>Canceled</option>
                  <option value='historical'>Past</option>
                </Select>
              </FormControl>
              <Button
                variant='ghost'
                flexShrink={0}
                isDisabled={
                  editing || (!searchText && status === 'current_and_upcoming' && !hasColumnFilters)
                }
                onClick={() => {
                  setSearchText('')
                  setQuery('')
                  setStatus('current_and_upcoming')
                  gridApi?.setFilterModel(null)
                }}
              >
                Clear filters
              </Button>
            </Stack>
            {homeId && (
              <Text fontSize='sm'>
                Showing this home’s stored vacancies.{' '}
                <Link as={RouterLink} to='/vacancy-readiness' color='teal.700'>
                  Browse all homes
                </Link>
              </Text>
            )}
            <HStack justify='flex-end' minH={6}>
              <HStack role='status' flexShrink={0}>
                {loading && <Spinner size='sm' aria-label='Updating vacancies' />}
                <Text fontSize='sm'>
                  {total === null
                    ? error
                      ? 'Could not load vacancies'
                      : 'Loading vacancies…'
                    : `${total.toLocaleString()} ${total === 1 ? 'vacancy' : 'vacancies'}`}
                </Text>
              </HStack>
            </HStack>
            {error && (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                <Stack spacing={2}>
                  <Text>{error}</Text>
                  <Button
                    size='sm'
                    alignSelf='start'
                    onClick={() => setRefresh((value) => value + 1)}
                  >
                    Try again
                  </Button>
                </Stack>
              </Alert>
            )}
            <Box ref={gridContainer} role='region' aria-label='Vacancies' minW={0}>
              <Grid<ReadinessSelection>
                columns={columns}
                rowData={rows}
                onGridReady={onGridReady}
                containerStyle={{ height: gridHeight, minHeight: 640 }}
                defaultColDefOverrides={defaultColumn}
                optionOverrides={gridOptions}
                getRowId={({ data }) => data.vacancy_period_id}
                onRowDataUpdated={({ api }) => {
                  if (rows.length && scrollKey.current !== `${browseKey}:${page}`) {
                    api.ensureIndexVisible(0, 'top')
                    scrollKey.current = `${browseKey}:${page}`
                  }
                }}
                onSortChanged={({ columnApi }) => {
                  const column = columnApi.getColumnState().find((item) => item.sort)
                  setSort({ colId: column?.colId || 'state', direction: column?.sort || 'asc' })
                }}
                onFilterChanged={({ api }) => {
                  const model = api.getFilterModel()
                  const next = selectedFilters(model)
                  setFilters((current) =>
                    JSON.stringify(current) === JSON.stringify(next) ? current : next
                  )
                  setHasColumnFilters(Object.keys(model).length > 0)
                  setSelected(null)
                  setDetailsOpen(false)
                }}
                onRowClicked={({ data, event }) => {
                  if (event?.target instanceof Element && event.target.closest('a, button')) return
                  if (data) openDetails(data)
                }}
                onCellKeyDown={({ data, event }) => {
                  if (event?.target instanceof Element && event.target.closest('a, button')) return
                  if (data && (event as KeyboardEvent)?.key === 'Enter') openDetails(data)
                }}
                overlayNoRowsTemplate={
                  query && status !== 'all'
                    ? 'No vacancies match these filters. Try a different address or vacancy status.'
                    : query
                    ? 'No vacancies match this address. Try a shorter search or check the spelling.'
                    : status !== 'all'
                    ? 'No vacancies match this status.'
                    : 'No stored vacancies are available. Missing records need reconciliation.'
                }
              />
            </Box>
            <HStack justify='space-between' aria-label='Vacancy pages'>
              <Text fontSize='sm' role='status'>
                {total === null
                  ? loading
                    ? 'Loading vacancies…'
                    : 'Vacancies unavailable'
                  : `${total ? (page - 1) * PAGE_SIZE + 1 : 0}–${Math.min(
                      page * PAGE_SIZE,
                      total
                    )} of ${total.toLocaleString()} vacancies`}
              </Text>
              <HStack>
                <Button
                  size='sm'
                  variant='outline'
                  isDisabled={loading || !!error || page === 1 || editing}
                  onClick={() => setCursor({ key: browseKey, page: page - 1 })}
                >
                  Previous
                </Button>
                <Text fontSize='sm'>
                  Page {page}
                  {total !== null ? ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}` : ''}
                </Text>
                <Button
                  size='sm'
                  variant='outline'
                  isDisabled={
                    loading || !!error || total === null || page * PAGE_SIZE >= total || editing
                  }
                  onClick={() => setCursor({ key: browseKey, page: page + 1 })}
                >
                  Next
                </Button>
              </HStack>
            </HStack>
          </>
        )}
      </Stack>
      <Drawer
        isOpen={canRead && detailsOpen && !!selected}
        onClose={closeDetails}
        size='xl'
        closeOnEsc={!editing}
        closeOnOverlayClick={!editing}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton isDisabled={editing} />
          <DrawerHeader borderBottomWidth={1}>Vacancy details</DrawerHeader>
          <DrawerBody py={5}>
            {canRead && selected && (
              <ReadinessPanel
                key={selected.vacancy_period_id}
                vacancyPeriodId={selected.vacancy_period_id}
                onEditingChange={setEditing}
                onSaved={() => setRefresh((value) => value + 1)}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </DynamicWidthPageContainer>
  )
}

export default VacancyReadiness
